import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const COLORS = {
  bg: '#FFFFFF',
  bgAlt: '#F8FAFC',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  borderHover: '#CBD5E1',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  fuchsia: '#D946EF'
};

const SparkIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="sparkGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path
      d="M12 2l2.39 6.21L21 10.5l-5.4 3.84L17 21l-5-3.36L7 21l1.4-6.66L3 10.5l6.61-2.29L12 2z"
      fill="url(#sparkGrad)"
    />
  </svg>
);

const ShieldCheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="shieldGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path
      d="M12 2l8 3v6c0 5-3.5 9.3-8 11-4.5-1.7-8-6-8-11V5l8-3z"
      fill="url(#shieldGrad)"
    />
    <path
      d="M8.5 12l2.5 2.5L15.5 10"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const HandoffIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="handoffGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path
      d="M3 8h11l-2-2m11 10H12l2 2"
      stroke="url(#handoffGrad)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="4" cy="16" r="2" fill="url(#handoffGrad)" />
    <circle cx="20" cy="8" r="2" fill="url(#handoffGrad)" />
  </svg>
);

const principles = [
  {
    Icon: SparkIcon,
    title: 'Zero to one',
    description: "We build what doesn't exist yet."
  },
  {
    Icon: ShieldCheckIcon,
    title: 'Production-grade',
    description: 'Not demos. Not notebooks. Real systems.'
  },
  {
    Icon: HandoffIcon,
    title: 'Handoff-ready',
    description: 'Your team can run it without us.'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  }
};

const Principles = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section
      id="principles"
      ref={ref}
      style={{
        backgroundColor: COLORS.bg,
        color: COLORS.textPrimary,
        padding: 'var(--spacing-8xl) 0'
      }}
    >
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          style={{ textAlign: 'center', marginBottom: 'var(--spacing-4xl)' }}
        >
          <motion.div variants={itemVariants}>
            <span
              style={{
                display: 'inline-block',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
                letterSpacing: 'var(--tracking-widest)',
                textTransform: 'uppercase',
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet}, ${COLORS.fuchsia})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 'var(--spacing-lg)'
              }}
            >
              Principles
            </span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              fontWeight: 700,
              color: COLORS.textPrimary,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              marginBottom: 'var(--spacing-lg)'
            }}
          >
            What We Believe
          </motion.h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 'var(--spacing-xl)'
          }}
        >
          {principles.map(({ Icon, title, description }) => (
            <motion.div
              key={title}
              variants={itemVariants}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              style={{
                backgroundColor: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--spacing-2xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-md)',
                transition: 'border-color 250ms ease, box-shadow 250ms ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = COLORS.borderHover;
                e.currentTarget.style.boxShadow =
                  '0 12px 28px -10px rgba(99, 102, 241, 0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = COLORS.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon size={22} />
              </div>

              <h3
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  letterSpacing: '-0.015em',
                  margin: 0
                }}
              >
                {title}
              </h3>

              <p
                style={{
                  fontSize: 'var(--text-base)',
                  color: COLORS.textSecondary,
                  lineHeight: 1.6,
                  margin: 0
                }}
              >
                {description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Principles;
